import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useMemo } from 'react';
import {
	format,
	startOfMonth,
	endOfMonth,
	startOfWeek,
	endOfWeek,
	addMonths,
	subMonths,
	eachDayOfInterval,
	isSameMonth,
	isToday,
	getDay,
} from 'date-fns';

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatSignedDollars = (value) => {
	const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(Math.abs(value || 0));
	return (value || 0) >= 0 ? `+${formatted}` : `-${formatted}`;
};

export default function CalendarHeatmap({ data }) {
	const [currentMonth, setCurrentMonth] = useState(() => {
		if (!data || data.length === 0) return new Date();
		const dates = data.map((d) => new Date(d.date + 'T00:00:00'));
		return startOfMonth(dates[dates.length - 1]);
	});

	const dailyMap = useMemo(() => {
		const map = {};
		if (!data) return map;
		for (const d of data) {
			const dateStr = d.date.slice(0, 10);
			map[dateStr] = d;
		}
		return map;
	}, [data]);

	const monthStart = startOfMonth(currentMonth);
	const monthEnd = endOfMonth(currentMonth);
	const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
	const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
	const allDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

	const weeks = [];
	for (let i = 0; i < allDays.length; i += 7) {
		weeks.push(allDays.slice(i, i + 7));
	}

	const maxPnl = useMemo(() => {
		if (!data || data.length === 0) return 1;
		return Math.max(...data.map((d) => Math.abs(d.profit)), 1);
	}, [data]);

	const monthStats = useMemo(() => {
		let totalPnl = 0;
		let totalTrades = 0;
		let tradingDays = 0;
		for (const day of allDays) {
			if (!isSameMonth(day, currentMonth)) continue;
			const key = format(day, 'yyyy-MM-dd');
			const d = dailyMap[key];
			if (d) {
				totalPnl += d.profit;
				totalTrades += d.trades;
				tradingDays++;
			}
		}
		return { totalPnl, totalTrades, tradingDays };
	}, [allDays, currentMonth, dailyMap]);

	const weekSummaries = useMemo(() => {
		return weeks.map((week) => {
			let pnl = 0;
			let trades = 0;
			let days = 0;
			for (const day of week) {
				const key = format(day, 'yyyy-MM-dd');
				const d = dailyMap[key];
				if (d) {
					pnl += d.profit;
					trades += d.trades;
					days++;
				}
			}
			return { pnl, trades, days };
		});
	}, [weeks, dailyMap]);

	if (!data || data.length === 0) {
		return (
			<Card>
				<CardHeader>
					<CardTitle className="text-sm">Trading Calendar</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="text-muted-foreground flex min-h-[200px] items-center justify-center text-sm">
						No data available
					</div>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card>
			<CardHeader className="pb-3">
				<div className="flex items-center justify-between">
					<CardTitle className="text-sm">Trading Calendar</CardTitle>
					<div className="flex items-center gap-3">
						<span
							className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
								monthStats.totalPnl >= 0
									? 'bg-emerald-500/20 text-emerald-500'
									: 'bg-red-500/20 text-red-500'
							}`}
						>
							{formatSignedDollars(monthStats.totalPnl)}
						</span>
						<span className="text-muted-foreground text-xs">
							{monthStats.totalTrades}T
						</span>
						<span className="text-muted-foreground text-xs">
							{monthStats.tradingDays} days
						</span>
					</div>
				</div>
				<div className="mt-2 flex items-center gap-2">
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}>
						<ChevronLeft className="h-4 w-4" />
					</Button>
					<span className="min-w-[130px] text-center text-sm font-medium">
						{format(currentMonth, 'MMMM yyyy')}
					</span>
					<Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}>
						<ChevronRight className="h-4 w-4" />
					</Button>
					<Button variant="outline" size="sm" className="ml-1 h-7 text-xs" onClick={() => setCurrentMonth(startOfMonth(new Date()))}>
						Today
					</Button>
				</div>
			</CardHeader>
			<CardContent className="pt-0">
				<div className="overflow-x-auto">
					<table className="w-full table-fixed border-collapse">
						<thead>
							<tr>
								{DAY_HEADERS.map((day) => (
									<th key={day} className="text-muted-foreground pb-1 text-center text-[10px] font-medium uppercase tracking-wider">
										{day}
									</th>
								))}
								<th className="text-muted-foreground pb-1 text-center text-[10px] font-medium uppercase tracking-wider">
									Week
								</th>
							</tr>
						</thead>
						<tbody>
							{weeks.map((week, wi) => {
								const ws = weekSummaries[wi];
								return (
									<tr key={wi}>
										{week.map((day) => {
											const key = format(day, 'yyyy-MM-dd');
											const d = dailyMap[key];
											const inMonth = isSameMonth(day, currentMonth);
											const today = isToday(day);
											const dow = getDay(day);
											const isWeekend = dow === 0 || dow === 6;

											if (!inMonth) {
												return (
													<td key={key} className="p-0.5">
														<div className="bg-muted/20 flex h-[60px] flex-col items-center justify-start rounded p-1 opacity-30">
															<span className="text-muted-foreground text-[10px]">{format(day, 'd')}</span>
														</div>
													</td>
												);
											}

											if (!d || isWeekend) {
												return (
													<td key={key} className="p-0.5">
														<div className={`flex h-[60px] flex-col items-center justify-start rounded p-1 ${today ? 'ring-primary ring-1' : ''} ${isWeekend ? 'bg-muted/10' : 'bg-muted/30'}`}>
															<span className={`text-[10px] ${today ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
																{format(day, 'd')}
															</span>
														</div>
													</td>
												);
											}

											const intensity = Math.min(1, Math.abs(d.profit) / maxPnl);
											const isPositive = d.profit >= 0;
											const bgColor = isPositive
												? `rgba(34, 197, 94, ${0.12 + intensity * 0.5})`
												: `rgba(239, 68, 68, ${0.12 + intensity * 0.5})`;

											return (
												<td key={key} className="p-0.5">
													<div
														className={`flex h-[60px] flex-col items-center justify-start rounded p-1 ${today ? 'ring-primary ring-1' : ''}`}
														style={{ background: bgColor }}
														title={`${key}: ${formatSignedDollars(d.profit)} (${d.trades} trades)`}
													>
														<span className={`text-[10px] ${today ? 'text-primary font-bold' : 'text-foreground/60'}`}>
															{format(day, 'd')}
														</span>
														<span className="mt-0.5 text-[11px] font-bold text-foreground/90">
															{formatSignedDollars(d.profit)}
														</span>
														<span className="text-foreground/50 text-[9px]">
															{d.trades}T
														</span>
													</div>
												</td>
											);
										})}
										<td className="p-0.5">
											{ws.trades > 0 ? (
												<div className="flex h-[60px] flex-col items-center justify-center rounded p-1">
													<span className={`text-[11px] font-bold ${ws.pnl >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
														{formatSignedDollars(ws.pnl)}
													</span>
													<span className="text-muted-foreground text-[9px]">{ws.trades}T</span>
													<span className="text-muted-foreground text-[9px]">{ws.days} days</span>
												</div>
											) : (
												<div className="h-[60px]" />
											)}
										</td>
									</tr>
								);
							})}
						</tbody>
					</table>
				</div>
			</CardContent>
		</Card>
	);
}
